import qs from 'qs'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { concat, EMPTY, from, of } from 'rxjs'
import { catchError, switchMap, map, filter } from 'rxjs/operators'
import { ActionsObservable, AppState } from '../index'

import { Action } from 'redux'
import { selectIfChatbotInitialized, selectIfChatbotLoading } from './index'
import {
  openSierra,
  openZva,
  sierraFailed,
  sierraInitialized,
  sierraLoading,
  sierraUpdated,
  startSierra,
  startZva,
  zvaFailed,
  zvaInitialized,
  zvaLoading,
  zvaUpdated,
} from './actions'
import { initZVA, openSierraWithConfig, openZVAWithConfig, updateSierra } from './effects'
import { initAndOpenSierra } from './effects'
import { Actions } from '../actionTypes'

const helpCenterUrl = {
  us: 'https://support.fubo.tv/hc',
  canada: 'https://support.fubo.tv/hc/en-ca',
  all: 'https://support.fubo.tv/hc',
  latino: 'https://support.fubo.tv/hc',
}

const getConfig = (state: AppState) => {
  const { market } = state
  return {
    authenticated: false,
    location_info: {
      current_country: market.countryCode || '',
      current_zip: market.postalCode || '',
    },
  }
}

/**
 * Starts zva if the zva param is present.
 * We need this so zva can push the user to a new location and we can
 * "keep it open" when we are essentially reinitializing the app entirely.
 *
 * Remove the param once zva launches because we have no way of detecting
 * if ZVA is closed and don't want a page refresh later to automatically open ZVA.
 */
const startZvaOnAppReadyEpic = (action$: ActionsObservable): any =>
  action$.pipe(
    ofType(Actions.ANALYTICS_READY),
    switchMap(() => {
      // Make hydration-safe by checking if we're in browser
      const isBrowser = typeof window !== 'undefined'
      if (!isBrowser) {
        return EMPTY
      }

      const currentLocation = window.location
      const currentSearch = currentLocation?.search || ''
      const parsedSearch = qs.parse(currentSearch, {
        ignoreQueryPrefix: true,
      })
      if (parsedSearch.zva !== 'true') {
        return EMPTY
      }
      const currentPathname = currentLocation?.pathname || ''
      const modifiedSearch = qs.stringify({
        ...parsedSearch,
        zva: undefined,
      })
      const redirectUrl = `${currentPathname}${modifiedSearch?.length ? `?${modifiedSearch}` : ''}`
      window.history.replaceState(null, '', redirectUrl)
      return of(startZva())
    })
  )
const startZvaEpic = (action$: ActionsObservable, state$: StateObservable<AppState>): any =>
  action$.pipe(
    ofType(Actions.ZVA_START),
    switchMap(() => {
      const state = state$.value
      const zvaIsInitialized = selectIfChatbotInitialized(state)
      const zvaIsLoading = selectIfChatbotLoading(state)
      if (zvaIsInitialized) {
        return of(openZva())
      }
      if (zvaIsLoading) {
        return EMPTY
      }
      const config = getConfig(state)
      return concat(
        of(zvaLoading()),
        from(initZVA(config)).pipe(
          map(zvaInitialized),
          catchError((err) => of(zvaFailed(err)))
        )
      )
    })
  )
const openZvaWhenReadyEpic = (action$: ActionsObservable, state$: StateObservable<AppState>): any =>
  action$.pipe(
    ofType(Actions.ZVA_OPEN, Actions.ZVA_INITIALIZED),
    switchMap(() => {
      const state = state$.value
      const config = getConfig(state)

      return from(openZVAWithConfig(config)).pipe(map(zvaUpdated))
    })
  )

const triggerZvaUpdateEpic = (
  action$: ActionsObservable,
  state$: StateObservable<AppState>
): any => {
  const state = state$.value
  const updateAction = updateSierra
  const updateCompletedAction = sierraUpdated

  return action$.pipe(
    ofType(Actions.SET_MARKET_SUCCESS),
    filter(() => !!state$.value.chatbot.isInitialized),
    switchMap(() => {
      const config = getConfig(state)
      return from(updateAction(config)).pipe(map(updateCompletedAction))
    })
  )
}
const redirectOnFailedEpic = (action$: ActionsObservable, state$: StateObservable<AppState>): any =>
  action$.pipe(
    ofType(Actions.ZVA_FAILED),
    switchMap(() => {
      // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'typeof StateObservable'.
      const { market } = state$.value.market
      const redirectUrl = helpCenterUrl?.[market]
        ? `${helpCenterUrl[market]}/requests/new`
        : helpCenterUrl.all
      window.open(redirectUrl, '_blank')
      return EMPTY
    })
  )

// Sierra
const startSierraOnAppReadyEpic = (action$: ActionsObservable): any =>
  action$.pipe(
    ofType(Actions.ANALYTICS_READY),
    switchMap(() => {
      const currentLocation = window?.location
      const currentSearch = currentLocation?.search || ''
      const parsedSearch = qs.parse(currentSearch, {
        ignoreQueryPrefix: true,
      })
      if (parsedSearch.sierra !== 'true') {
        return EMPTY
      }
      const currentPathname = currentLocation?.pathname || ''
      const modifiedSearch = qs.stringify({
        ...parsedSearch,
        sierra: undefined,
      })
      const redirectUrl = `${currentPathname}${modifiedSearch?.length ? `?${modifiedSearch}` : ''}`
      window.history.replaceState(null, '', redirectUrl)
      return of(startSierra())
    })
  )

export const startSierraEpic = (
  action$: ActionsObservable,
  state$: StateObservable<AppState>
): any =>
  action$.pipe(
    ofType(Actions.SIERRA_START),
    switchMap(() => {
      const state = state$.value
      if (selectIfChatbotInitialized(state)) {
        return of(openSierra())
      }
      if (selectIfChatbotLoading(state)) {
        return EMPTY
      }
      const config = getConfig(state)

      return concat(
        of(sierraLoading()),
        from(initAndOpenSierra(config)).pipe(
          map(() => sierraInitialized()),
          catchError((err) => of(sierraFailed(err)))
        )
      )
    })
  )

export const openSierraWhenReadyEpic = (
  action$: ActionsObservable,
  state$: StateObservable<AppState>
): any =>
  action$.pipe(
    ofType(Actions.SIERRA_OPEN),
    switchMap(() => {
      const state = state$.value
      const config = getConfig(state)
      return from(openSierraWithConfig(config)).pipe(
        map(sierraUpdated),
        catchError((err) => of(sierraFailed(err)))
      )
    })
  )

export const chatbotEpic = combineEpics<Action, Action, AppState>(
  startZvaOnAppReadyEpic,
  startZvaEpic,
  openZvaWhenReadyEpic,
  triggerZvaUpdateEpic,
  redirectOnFailedEpic,
  startSierraOnAppReadyEpic,
  startSierraEpic,
  openSierraWhenReadyEpic
)
